import { forwardRef, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import Loading from "../Loading";
import {
  AnalyticsTrackingEvent,
  ArrowRightIcon,
  Button,
  ButtonProps,
} from "@brandclub/common-ui";
import { useTrackActions } from "../../utils/hooks/useTracking";
import { useAppSelector } from "../../redux/hooks";
import { ThemeColorKey } from "@brandclub/common-ui/theme/types";

export type TrackedMotionButtonProps = ButtonProps & {
  trackedEventName?: AnalyticsTrackingEvent;
  trackedAdditionalData?: Record<string, any>;
  disableMaxWidth?: boolean;
  color?: ThemeColorKey;
};

export const TrackedMotionButton = forwardRef<
  HTMLButtonElement,
  TrackedMotionButtonProps
>(({ trackedEventName, trackedAdditionalData, onClick, ...props }, ref) => {
  const [trackAction] = useTrackActions();
  const domainConfig = useAppSelector((state) => state.appConfig?.domainConfig);
  const mainEntity = useAppSelector((state) => state.mainEntity);
  const brandEntity = useAppSelector((state) => state.brandEntity);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    if (trackedEventName) {
      trackAction(trackedEventName, {
        brandName: mainEntity?.entity.brandName,
        brandId: mainEntity?.entity.brandId,
        brandclubId: brandEntity?.brandclub?.brandclubId,
        appId: domainConfig?.domainName,
        ...trackedAdditionalData,
      });
    }
    if (onClick) {
      trackAction?.(AnalyticsTrackingEvent.BUTTON_CLICK, {
        buttonName: (event?.target as HTMLButtonElement)?.innerText,
      });
      onClick(event);
    }
  };

  return <Button ref={ref} onClick={handleClick} {...props} />;
});

TrackedMotionButton.displayName = "TrackedMotionButton";

export const SolidButton = forwardRef<
  HTMLButtonElement,
  Omit<TrackedMotionButtonProps, "variant">
>((props, ref) => {
  return (
    <TrackedMotionButton ref={ref} color="primary" {...props} variant="solid" />
  );
});

SolidButton.displayName = "SolidButton";
export const OutlineButton = forwardRef<
  HTMLButtonElement,
  Omit<TrackedMotionButtonProps, "variant">
>((props, ref) => {
  return (
    <TrackedMotionButton
      ref={ref}
      color="primary"
      {...props}
      variant="outline"
    />
  );
});

OutlineButton.displayName = "OutlineButton";

export const ButtonLoadingEllipsis = ({
  baseFontSize = 12,
  blue,
  customColor,
  style,
}: {
  baseFontSize?: number;
  blue?: boolean;
  customColor?: string;
  style?: React.CSSProperties;
}) => {
  const ellipsisContainerSize = baseFontSize * 1.4;
  const ellipsisSize = baseFontSize * 2.6;
  const yOffset = ellipsisSize / 10;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        width: ellipsisContainerSize,
        height: ellipsisContainerSize,
        minWidth: ellipsisContainerSize,
        minHeight: ellipsisContainerSize,
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          width: ellipsisSize,
          height: ellipsisSize,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: `translateY(${yOffset}px)`,
        }}
      >
        <Loading
          ellipsis={!blue}
          ellipsisBlue={blue}
          size={ellipsisSize}
          ellipsisCustomColor={customColor}
        />
      </div>
    </div>
  );
};

export const SolidButtonWithArrow = forwardRef<
  HTMLButtonElement,
  Omit<TrackedMotionButtonProps, "variant">
>((props, ref) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Button
      color="primary"
      ref={ref}
      {...props}
      variant="solid"
      onMouseLeave={() => {
        setHovered(false);
      }}
      onMouseEnter={() => {
        setHovered(true);
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          style={{
            display: "flex",
            alignItems: "center",
          }}
          key={hovered ? "arrow" : "content"}
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 30, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {hovered ? (
            <ArrowRightIcon
              style={{
                marginLeft: 0,
                width: 20,
                height: 20,
              }}
            />
          ) : (
            (props.children as ReactNode)
          )}
        </motion.div>
      </AnimatePresence>
    </Button>
  );
});

export const OutlineButtonWithArrow = forwardRef<
  HTMLButtonElement,
  Omit<TrackedMotionButtonProps, "variant">
>((props, ref) => {
  const [hovered, setHovered] = useState(false);
  const arrowAnimations = {
    hover: {
      x: ["-50%", "50%"],
      opacity: [0, 1],
      transition: {
        duration: 0.25,
        delay: 0.25,
      },
    },
    unhover: {
      x: ["50%", "150%"],
      opacity: [1, 0],
      transition: {
        duration: 0.25,
      },
    },
  };

  const textAnimations = {
    hover: {
      x: ["0%", "15%"],
      opacity: [1, 0],
      transition: {
        duration: 0.25,
      },
    },
    unhover: {
      x: ["-15%", "0%"],
      opacity: [0, 1],
      transition: {
        duration: 0.25,
        delay: 0.25,
      },
    },
  };

  return (
    <TrackedMotionButton
      color="primary"
      ref={ref}
      {...props}
      variant="outline"
      onMouseLeave={() => {
        setHovered(false);
      }}
      onMouseEnter={() => {
        setHovered(true);
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        <motion.div
          style={{
            position: "absolute",
            right: "50%",
            width: 20,
            height: 20,
          }}
          animate={hovered ? "hover" : "unhover"}
          variants={arrowAnimations}
        >
          <ArrowRightIcon />
        </motion.div>

        <motion.div
          animate={hovered ? "hover" : "unhover"}
          variants={textAnimations}
        >
          {props.children as ReactNode}
        </motion.div>
      </div>
    </TrackedMotionButton>
  );
});
